import { useArrowStore, ConnectorPosition } from '@/stores/arrowStore';
import { useCanvasStore } from '@/stores/canvasStore';

/**
 * Where an arrow being drawn ends on release: the connector dot under the
 * pointer, or else the card under the pointer, on the side nearest to it.
 * The start card only counts through an exact dot, and never after
 * "Connect to..." (that would connect a card to itself).
 */
export function findArrowEnd(
  e: Pick<MouseEvent, 'clientX' | 'clientY'>,
  startItemId: string | null,
  fromMenu?: boolean,
): { itemId: string; position: ConnectorPosition } | null {
  const { hoveredConnector } = useArrowStore.getState();
  if (hoveredConnector && !(fromMenu && hoveredConnector.itemId === startItemId)) {
    return hoveredConnector;
  }

  const under = document.elementFromPoint(e.clientX, e.clientY);
  const card = under?.closest<HTMLElement>('[data-item-id]');
  const itemId = card?.dataset.itemId;
  if (!card || !itemId || itemId === startItemId) return null;
  if (!useCanvasStore.getState().items.some((i) => i.id === itemId)) return null;

  const r = card.getBoundingClientRect();
  const sides: { position: ConnectorPosition; x: number; y: number }[] = [
    { position: 'top', x: r.left + r.width / 2, y: r.top },
    { position: 'bottom', x: r.left + r.width / 2, y: r.bottom },
    { position: 'left', x: r.left, y: r.top + r.height / 2 },
    { position: 'right', x: r.right, y: r.top + r.height / 2 },
  ];
  let best = sides[0];
  let bestDist = Infinity;
  for (const side of sides) {
    const d = (side.x - e.clientX) ** 2 + (side.y - e.clientY) ** 2;
    if (d < bestDist) {
      bestDist = d;
      best = side;
    }
  }
  return { itemId, position: best.position };
}
